
import React, { useState } from 'react';
import { Share2, Users, Award, BellDot } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ReferralList from '@/components/ReferralList';
import ReferralForm from '@/components/ReferralForm';
import { type User as UserType } from '@/types/userRole';
import ReferralStatsChart from '@/components/dashboard/ReferralStatsChart';
import ReferralSourcePieChart from '@/components/dashboard/ReferralSourcePieChart';
import TaskNotification from '@/components/profile/TaskNotification';
import { useTaskNotifications } from '@/hooks/useTaskNotifications';

interface DashboardTabsProps {
  referrals: any[];
  referralLink: string;
  handleSendReferral: (data: any, method: string) => Promise<any>;
  user: UserType;
  points: number;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  chartData?: any[];
  referralSourceData?: any[];
}

const DashboardTabs: React.FC<DashboardTabsProps> = ({
  referrals,
  referralLink,
  handleSendReferral,
  user,
  points,
  activeTab = 'overview',
  onTabChange,
  chartData,
  referralSourceData
}) => {
  const [localActiveTab, setLocalActiveTab] = useState(activeTab);
  const { tasks, markTaskComplete } = useTaskNotifications(user);
  
  const handleTabChange = (value: string) => {
    setLocalActiveTab(value);
    if (onTabChange) {
      onTabChange(value);
    }
  };

  // Add icons to tasks
  const tasksWithIcons = tasks.map(task => ({
    ...task,
    icon: task.id === 'profile' ? <Users size={16} /> : 
          task.id === 'payment' ? <Award size={16} /> : <Share2 size={16} />
  }));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <Tabs 
          id="referrals-tab" 
          defaultValue={activeTab} 
          value={onTabChange ? activeTab : localActiveTab} 
          onValueChange={handleTabChange} 
          className="w-full"
        >
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-50">
              <Users className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="send" className="data-[state=active]:bg-blue-50">
              <Share2 className="h-4 w-4 mr-2" />
              Send Referral
            </TabsTrigger>
            <TabsTrigger value="points" className="data-[state=active]:bg-blue-50">
              <Award className="h-4 w-4 mr-2" />
              Points
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center">
                  <Award className="h-5 w-5 mr-2 text-amber-500" />
                  Your Referrals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ReferralList referrals={referrals} />
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Referral Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <ReferralStatsChart data={chartData || []} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Referral Sources</CardTitle>
                </CardHeader>
                <CardContent>
                  <ReferralSourcePieChart data={referralSourceData || []} />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="send" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Share2 className="h-5 w-5 mr-2 text-green-500" />
                  Send a New Referral
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ReferralForm 
                  referralLink={referralLink}
                  referralTemplate={`Check out this referral link: {referralLink}`}
                  onSendReferral={handleSendReferral} 
                />
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="points" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Award className="h-5 w-5 mr-2 text-amber-500" />
                  Your Points: {points}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p>You have earned {points} points through your referrals.</p>
                  <Link to="/user/points-redemption">
                    <Button className="w-full">
                      <Award className="h-4 w-4 mr-2" />
                      Redeem Points
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        {/* Add Task Notification component */}
        <div className="flex-shrink-0">
          <TaskNotification
            tasks={tasksWithIcons}
            onTaskComplete={markTaskComplete}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardTabs;
